import React from "react";
import {
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  TextField,
} from "@mui/material";
import { ConfigSection } from "../ConfigSection";
import { StyledTextField } from "../StyledTextField";
import { createRangeValidator } from "../../utils/validation";
import {
  AdvancedSearchConfigDto,
  ScriptScoreConfigDto,
} from "@swagger/typescript-fetch-goflyer";
import { COLORS } from "../../constants";

export const ScriptScoreConfigSection: React.FC<{
  config: {
    scriptScoreConfig?: ScriptScoreConfigDto;
    boostMode?: AdvancedSearchConfigDto["boostMode"];
  };
  updateScriptScoreConfig: (updates: Partial<ScriptScoreConfigDto>) => void;
  updateBoostMode: (mode?: AdvancedSearchConfigDto["boostMode"]) => void;
}> = ({ config, updateScriptScoreConfig, updateBoostMode }) => {
  const scriptScoreConfig = config.scriptScoreConfig || {};

  const normalizeCombineMode = (value: unknown): string => {
    if (value === undefined || value === null) return "max";
    if (typeof value === "string") return value.toLowerCase();
    if (typeof value === "number") {
      return value === 0 ? "max" : value === 1 ? "sum" : "multiply";
    }
    return "max";
  };

  const normalizeBoostMode = (value: unknown): string => {
    if (value === undefined || value === null) return "replace";
    if (typeof value === "string") return value.toLowerCase();
    if (typeof value === "number") {
      switch (value) {
        case 0:
          return "replace";
        case 1:
          return "multiply";
        case 2:
          return "sum";
        case 3:
          return "avg";
        case 4:
          return "max";
        case 5:
          return "min";
        default:
          return "replace";
      }
    }
    return "replace";
  };

  const combineModeValue = normalizeCombineMode(
    scriptScoreConfig.combineMode as unknown
  );
  const boostModeValue = normalizeBoostMode(config.boostMode as unknown);
  return (
    <ConfigSection
      title="Script Score Configuration"
      tooltip="Applies custom rules for ranking results beyond the standard search algorithm."
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={!!scriptScoreConfig.enable}
                onChange={(event) =>
                  updateScriptScoreConfig({ enable: event.target.checked })
                }
              />
            }
            label="Enable script-based scoring"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: COLORS.PRIMARY,
                "&:hover": {
                  backgroundColor: COLORS.PRIMARY_ALPHA,
                },
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: COLORS.PRIMARY,
              },
            }}
          />
        </Grid>

        {scriptScoreConfig.enable && (
          <>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Exact Match Multiplier"
                type="number"
                inputProps={{ step: 0.1, min: 1, max: 10000 }}
                value={scriptScoreConfig.exactMatchMultiplier ?? ""}
                validate={createRangeValidator(1, 10000)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const valueString = event.target.value;
                  updateScriptScoreConfig({
                    exactMatchMultiplier:
                      valueString === "" ? undefined : parseFloat(valueString),
                  });
                }}
                helperText="Weight when the search term matches exactly. Min: 1, Max: 10,000, Step: 0.1."
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Starts With Multiplier"
                type="number"
                inputProps={{ step: 0.1, min: 1, max: 10000 }}
                value={scriptScoreConfig.startsWithMultiplier ?? ""}
                validate={createRangeValidator(1, 10000)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const valueString = event.target.value;
                  updateScriptScoreConfig({
                    startsWithMultiplier:
                      valueString === "" ? undefined : parseFloat(valueString),
                  });
                }}
                helperText="Weight for results starting with the search term. Min: 1, Max: 10,000, Step: 0.1."
              />
            </Grid>

            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Contains Base"
                type="number"
                inputProps={{ step: 0.1, min: 0, max: 1000 }}
                value={scriptScoreConfig.containsBase ?? ""}
                validate={createRangeValidator(0, 1000)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const valueString = event.target.value;
                  updateScriptScoreConfig({
                    containsBase:
                      valueString === "" ? undefined : parseFloat(valueString),
                  });
                }}
                helperText="Base weight for matches where the term appears anywhere. Min: 0, Max: 1,000, Step: 0.1."
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Max Boost Cap"
                type="number"
                inputProps={{ step: 0.1, min: 1, max: 100000 }}
                value={scriptScoreConfig.maxBoostCap ?? ""}
                validate={createRangeValidator(1, 100000)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const valueString = event.target.value;
                  updateScriptScoreConfig({
                    maxBoostCap:
                      valueString === "" ? undefined : parseFloat(valueString),
                  });
                }}
                helperText="Maximum cap for boosting to prevent inflated scores. Min: 1, Max: 100,000, Step: 0.1."
              />
            </Grid>

            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Boundary Bonus"
                type="number"
                inputProps={{ step: 0.1, min: 1, max: 100 }}
                value={scriptScoreConfig.boundaryBonus ?? ""}
                validate={createRangeValidator(1, 100)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const valueString = event.target.value;
                  updateScriptScoreConfig({
                    boundaryBonus:
                      valueString === "" ? undefined : parseFloat(valueString),
                  });
                }}
                helperText="Extra reward for word-boundary matches. Min: 1, Max: 100, Step: 0.1."
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Combine Mode"
                value={combineModeValue}
                onChange={(event) =>
                  updateScriptScoreConfig({
                    combineMode: event.target.value as any,
                  })
                }
                helperText="Method for combining custom script scores: max, sum, multiply."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: COLORS.PRIMARY,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: COLORS.PRIMARY,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: COLORS.PRIMARY,
                  },
                }}
              >
                <MenuItem value="max">max</MenuItem>
                <MenuItem value="sum">sum</MenuItem>
                <MenuItem value="multiply">multiply</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Boost Mode"
                value={boostModeValue}
                onChange={(event) => updateBoostMode(event.target.value as any)}
                helperText="Decides how custom scores combine with the default score: replace (only custom), multiply (balanced), sum (additive), avg (conservative), max (take higher), min (take lower). Default: replace."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: COLORS.PRIMARY,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: COLORS.PRIMARY,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: COLORS.PRIMARY,
                  },
                }}
              >
                <MenuItem value="replace">replace</MenuItem>
                <MenuItem value="multiply">multiply</MenuItem>
                <MenuItem value="sum">sum</MenuItem>
                <MenuItem value="avg">avg</MenuItem>
                <MenuItem value="max">max</MenuItem>
                <MenuItem value="min">min</MenuItem>
              </TextField>
            </Grid>
          </>
        )}
      </Grid>
    </ConfigSection>
  );
};
